import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { WorkerOnboardingStep } from '@/services/workersV2API';

type Props = {
  steps: WorkerOnboardingStep[];
};

const stepStatusColor: Record<string, string> = {
  pending: '#6B7280',
  in_progress: '#F59E0B',
  completed: '#10B981',
  rejected: '#EF4444',
  failed: '#EF4444',
  skipped: '#9CA3AF',
};

const stepStatusLabel: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  rejected: 'Rejected',
  failed: 'Failed',
  skipped: 'Skipped',
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const OnboardingStepsCard = ({ steps }: Props) => {
  if (!steps || steps.length === 0) {
    return (
      <Card title="Onboarding Steps">
        <Card.Section>
          <Body color="lighter">No onboarding steps data available</Body>
        </Card.Section>
      </Card>
    );
  }

  const completedSteps = steps.filter((s) => s.status === 'completed').length;
  const totalSteps = steps.length;
  const completionPercentage =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  return (
    <Card title="Onboarding Steps">
      <Card.Section>
        <Stack vertical gap={8}>
          <Stack style={{ justifyContent: 'space-between' }}>
            <Body weight="semibold">Progress</Body>
            <Body weight="semibold">
              {completedSteps} / {totalSteps}
            </Body>
          </Stack>
          <div
            style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#E5E7EB',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${completionPercentage}%`,
                height: '100%',
                backgroundColor: '#10B981',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <Small color="lighter">{completionPercentage}% Complete</Small>
        </Stack>
      </Card.Section>
      <Card.Section>
        <Stack vertical gap={12}>
          {steps.map((step) => (
            <Stack
              key={step.id}
              vertical
              gap={4}
              style={{
                padding: '12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                borderLeft: `4px solid ${stepStatusColor[step.status] || '#6B7280'}`,
              }}
            >
              <Stack
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Body weight="semibold">{step.title}</Body>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '4px 12px',
                    borderRadius: '4px',
                    backgroundColor:
                      (stepStatusColor[step.status] || '#6B7280') + '20',
                    color: stepStatusColor[step.status] || '#6B7280',
                  }}
                >
                  {stepStatusLabel[step.status] || step.status}
                </span>
              </Stack>
              {step.completed_at && (
                <Small color="lighter">
                  Completed: {formatDate(step.completed_at)}
                </Small>
              )}
            </Stack>
          ))}
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default OnboardingStepsCard;
